import type { CatalogItem } from "building-types/catalogItemTypes";
import type { Doc } from "building-types/doc";
import type { CatalogItemHandle, SettingsHandle } from "building-types/handles/docEntityHandleTypes";
import type { RWTable, Table } from "common/data/table";
import { LRUObjectCache } from "common/utils/lruObjectCache";
import { catalogSettingsTypeToCatalogFieldDescriptors } from "./catalogItemTools";
import type { CatalogSettings } from "./elements/catalog";
import { isCatalogSettings } from "./elements/catalog";
import { docGetTableEntity } from "./entity/docGetTableEntity";

interface CatalogItemCacheEntry {
  items: Table<CatalogItem>;
  parentByItemId: Table<SettingsHandle>;
  fieldNameByItemId: Table<string>;
}

// Keyed by `doc.settings` identity (the catalog table is derived from settings),
// so an O(1) keyFn replaces the previous `a.settings === b.settings` scan.
const catalogItemCache = new LRUObjectCache<Doc, CatalogItemCacheEntry>(10, undefined, undefined, {
  keyFn: (doc) => doc.settings,
});

function getItemsFromCatalogSettings(settings: CatalogSettings): Table<CatalogItem> {
  const descriptors = catalogSettingsTypeToCatalogFieldDescriptors[settings.type];

  if (!descriptors) {
    return {};
  }

  if (descriptors.length === 1) {
    return settings[descriptors[0].fieldName] ?? {};
  }

  return descriptors.reduce((acc, descriptor) => Object.assign(acc, settings[descriptor.fieldName]), {});
}

function buildCatalogItemCacheEntry(doc: Doc): CatalogItemCacheEntry {
  const items: RWTable<CatalogItem> = {};
  const parentByItemId: RWTable<SettingsHandle> = {};
  const fieldNameByItemId: RWTable<string> = {};

  for (const settingsId in doc.settings) {
    const settings = doc.settings[settingsId];

    if (!isCatalogSettings(settings)) {
      continue;
    }

    const settingsHandle: SettingsHandle = { type: "SETTINGS", id: settingsId };
    const descriptors = catalogSettingsTypeToCatalogFieldDescriptors[settings.type];

    if (!descriptors) {
      continue;
    }

    for (const descriptor of descriptors) {
      const table = settings[descriptor.fieldName] ?? {};

      for (const itemId in table) {
        items[itemId] = table[itemId];
        parentByItemId[itemId] = settingsHandle;
        fieldNameByItemId[itemId] = descriptor.fieldName;
      }
    }
  }

  return {
    items: items as Table<CatalogItem>,
    parentByItemId: parentByItemId as Table<SettingsHandle>,
    fieldNameByItemId: fieldNameByItemId as Table<string>,
  };
}

function getCatalogItemCacheEntry(doc: Doc): CatalogItemCacheEntry {
  let entry = catalogItemCache.get(doc);

  if (!entry) {
    entry = buildCatalogItemCacheEntry(doc);
    catalogItemCache.add(doc, entry);
  }

  return entry;
}

export function docGetCatalogItem(doc: Doc, item: CatalogItemHandle): CatalogItem | undefined {
  const { items } = getCatalogItemCacheEntry(doc);
  return items[item.itemId];
}

export function docForEachCatalogItem(doc: Doc, f: (handle: CatalogItemHandle, item: CatalogItem) => void) {
  const { items } = getCatalogItemCacheEntry(doc);

  for (const id in items) {
    f({ type: "CATALOG_ITEM", itemId: id }, items[id]);
  }
}

export function docGetItemsFromCatalog(doc: Doc, catalog: SettingsHandle): Table<CatalogItem> {
  const settings = docGetTableEntity(doc, catalog);

  if (!settings) {
    return {};
  }

  if (!isCatalogSettings(settings)) {
    return {};
  }

  return getItemsFromCatalogSettings(settings);
}

export function docGetCatalogItemParentCatalog(doc: Doc, itemHandle: CatalogItemHandle): SettingsHandle | undefined {
  const { parentByItemId } = getCatalogItemCacheEntry(doc);
  return parentByItemId[itemHandle.itemId];
}

export function docGetCachedCatalogItemTableFieldName(doc: Doc, itemHandle: CatalogItemHandle): string | undefined {
  const { fieldNameByItemId } = getCatalogItemCacheEntry(doc);
  return fieldNameByItemId[itemHandle.itemId];
}
